import {
  AddFluidsModel,
  AddFuelModel,
  Battery12VChargeModel,
  BatteryCheckModel,
  EvChargeModel,
  JobData,
  RecordActivityModel,
  TyrePressureModel,
  VehicleWashModel,
  WarmUpModel,
} from '@/types';
import { GalleryImages } from '@/ui/components/ImagesLightBox';
import { timeConverter } from '@/utils/helpers';
import { Col, Descriptions, Empty, Row } from 'antd';
import type { DescriptionsItemType } from 'antd/es/descriptions';
import React, { FC } from 'react';
import {
  engineTypes,
  fluidTypes,
  fuelTypes,
  recordActivityTypes,
  vehicleWashTypes,
  yesNoOptions,
} from '../constants';

interface JobInformationProps {
  job?: JobData;
}

export const JobInformation: FC<JobInformationProps> = ({ job }) => {
  if (!job) return <Empty />;

  const jobType = job.jobType;
  const data = job.maintenance?.data;
  if (!jobType || !data) return <Empty />;

  const images = job.maintenance?.images || [];
  type MaintenanceRenderer = (data: any) => DescriptionsItemType[];

  const renderValue = (value: unknown) =>
    value === undefined || value === null || value === '' ? '-' : String(value);

  const renderValueWithUnit = (value?: number, unit?: string) => {
    if (value === undefined || value === null) {
      return '-';
    }
    return unit ? `${value} ${unit}` : String(value);
  };

  const maintenanceRenderMap: Record<string, MaintenanceRenderer> = {
    WarmupEngine: (data: WarmUpModel) => [
      {
        key: 'targetMinutes',
        label: 'Minute Otto',
        children: data.targetMinutes,
      },
    ],

    EVCharge: (data: EvChargeModel) => [
      {
        key: 'target',
        label: 'Target',
        children: renderValueWithUnit(data.targetValue, data.inputMode),
      },
      {
        key: 'startValue',
        label: 'Starting Value',
        children: renderValueWithUnit(data.startValue, data.inputMode),
      },
      {
        key: 'endValue',
        label: 'Ending Value',
        children: renderValueWithUnit(data.endValue, data.inputMode),
      },
    ],

    Battery12VCharge: (data: Battery12VChargeModel) => [
      {
        key: 'startValue',
        label: 'Start Voltage',
        children: renderValueWithUnit(data.startValue, data.startUnit),
      },
      {
        key: 'endValue',
        label: 'End Voltage',
        children: renderValueWithUnit(data.endValue, data.endUnit),
      },
    ],

    AddFluids: (data: AddFluidsModel) => [
      {
        key: 'fluidType',
        label: 'Fluid Type',
        children: fluidTypes.find((f) => f.value === data.fluidType)?.label,
      },
      {
        key: 'quantity',
        label: 'Quantity',
        children: `${data.quantity} ${data.quantityType}`,
      },
      {
        key: 'reason',
        label: 'Reason',
        children: data.reason,
      },
    ],

    AddFuel: (data: AddFuelModel) => [
      {
        key: 'fuelType',
        label: 'Fuel Type',
        children: fuelTypes.find((f) => f.value === data.fuelType)?.label,
      },
      {
        key: 'quantity',
        label: 'Quantity',
        children: `${data.quantity} ${data.quantityType}`,
      },
      {
        key: 'comment',
        label: 'Comment',
        children: data.comment,
      },
    ],

    RecordActivity: (data: RecordActivityModel) => [
      {
        key: 'activity',
        label: 'Activity Type',
        children: recordActivityTypes.find(
          (r) => r.value === data.recordActivityType,
        )?.label,
      },
    ],

    VehicleWash: (data: VehicleWashModel) => [
      {
        key: 'washType',
        label: 'Wash Car',
        children: vehicleWashTypes.find((w) => w.value === data.vehicleWashType)
          ?.label,
      },
    ],

    BatteryCheck: (data: BatteryCheckModel) => [
      {
        key: 'engineType',
        label: 'Engine Type',
        children: engineTypes.find((e) => e.value === data.engineType)?.label,
      },
      {
        key: 'transportMode',
        label: 'Transport Mode',
        children: yesNoOptions.find((o) => o.value === data.transportMode)
          ?.label,
      },
      {
        key: 'battery12V',
        label: 'Is the 12V battery healthy?',
        children: renderValue(data.battery12VHealth),
      },
      {
        key: 'battery12VHealth',
        label: `What's the current 12V battery level?`,
        children: data.battery12VChargeRequired
          ? renderValueWithUnit(data.battery12VLevel, data.battery12VLevelUnit)
          : '---',
      },
      {
        key: 'battery12VHealthComment',
        label: 'Comment',
        children: data.battery12VHealthComment,
      },
      {
        key: 'charge12V',
        label: 'Is the HV battery healthy?',
        children: renderValue(data.batteryHVHealth),
      },
      {
        key: 'battery12VLevel',
        label: `What's the current HV battery SOC?`,
        children: data.batteryHVChargeRequired
          ? renderValueWithUnit(data.batteryHVSOC, data.batteryHVSOCUnit)
          : '---',
      },
      {
        key: 'batteryHVHealthComment',
        label: 'Battery HV Health Comment',
        children: data.batteryHVHealthComment,
      },
    ],

    TyrePressure: (data: TyrePressureModel) => [
      {
        key: 'tyrePressureThreshold',
        label: 'Threshold',
        children: renderValue(data.tyrePressureThreshold) + ' bar',
      },
      {
        key: 'tyrePressureSet',
        label: 'Pressure Set',
        children: renderValue(data.tyrePressureSet) + ' bar',
      },
      {
        key: 'nsfBar',
        label: 'NSF Pressure',
        children: renderValue(data.nsfBar) + ' bar',
      },
      {
        key: 'nsfAction',
        label: 'NSF Action',
        children: renderValue(data.nsfAction),
      },
      {
        key: 'nsfDone',
        label: 'NSF Status',
        children: renderValue(data.nsfDone),
      },
      {
        key: 'osfBar',
        label: 'OSF Pressure',
        children: renderValue(data.osfBar) + ' bar',
      },
      {
        key: 'osfAction',
        label: 'OSF Action',
        children: renderValue(data.osfAction),
      },
      {
        key: 'osfDone',
        label: 'OSF Status',
        children: renderValue(data.osfDone),
      },
      {
        key: 'nsrBar',
        label: 'NSR Pressure',
        children: renderValue(data.nsrBar) + ' bar',
      },
      {
        key: 'nsrAction',
        label: 'NSR Action',
        children: renderValue(data.nsrAction),
      },
      {
        key: 'nsrDone',
        label: 'NSR Status',
        children: renderValue(data.nsrDone),
      },
      {
        key: 'osrBar',
        label: 'OSR Pressure',
        children: renderValue(data.osrBar) + ' bar',
      },
      {
        key: 'osrAction',
        label: 'OSR Action',
        children: renderValue(data.osrAction),
      },
      {
        key: 'osrDone',
        label: 'OSR Status',
        children: renderValue(data.osrDone),
      },
    ],
  };

  const renderer = maintenanceRenderMap[data.maintenanceType!];
  const itemsTop: DescriptionsItemType[] = [
    {
      key: 'jobType',
      label: 'Job Type',
      children: job.jobTypeDescription,
      span: 2,
    },
    {
      key: 'startedAt',
      label: 'Start Time',
      children: timeConverter(data.startedAt),
    },
    {
      key: 'startedBy',
      label: 'Start By',
      children: data.startedBy,
    },
    {
      key: 'location',
      label: 'Location',
      children: job.location || '-',
    },
    {
      key: 'externalRef',
      label: 'External Reference',
      children: job.externalRef || '-',
    },
  ];
  const itemsBottom: DescriptionsItemType[] = [
    {
      key: 'notes',
      label: 'Notes',
      children: data.notes,
    },
    ...(data.failedAt
      ? [
          {
            key: 'failedAt',
            label: 'Failed Time',
            children: timeConverter(data.failedAt),
          },
          {
            key: 'failedBy',
            label: 'Failed By',
            children: data.failedBy,
          },
        ]
      : [
          {
            key: 'endedAt',
            label: 'End Time',
            children: timeConverter(data.endedAt || job.completedAt || ''),
          },
          {
            key: 'endedBy',
            label: 'End By',
            children: data.endedBy || job.completedBy,
          },
        ]),
  ];
  const rowItems: DescriptionsItemType[] = itemsTop
    .concat(renderer ? renderer(data) : [])
    .concat(itemsBottom);

  const getImageDescription = (imageType: string) => {
    switch (imageType) {
      case 'EVChargeStartingValue':
        return 'Starting';
      case 'EVChargeEndingValue':
        return 'Ending';
      case 'WashBefore':
        return 'Before';
      case 'WashAfter':
        return 'After';
      case 'Exception':
        return 'Exception';
      case 'Battery12VChargeStartVoltage':
        return 'Start';
      case 'Battery12VChargeEndVoltage':
        return 'End';
      default:
        return undefined;
    }
  };

  return (
    <Row gutter={16} className="mt-3">
      <Col xs={24} lg={12} className="overflow-auto" style={{ height: 300 }}>
        <Descriptions
          className="light-content"
          size="small"
          bordered
          column={1}
          items={rowItems}
          labelStyle={{ width: '200px' }}
        />
      </Col>
      <Col xs={24} lg={12}>
        {images.length > 0 ? (
          <div style={{ height: 300 }}>
            <GalleryImages
              actions={{ download: true }}
              showCaption
              images={images.map((image) => ({
                imageFileUrl: image.url,
                thumbnailImageUrl: image.resizedImageUrl,
                description: getImageDescription(image.imageType),
              }))}
            />
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100">
            <Empty description="No Images" />
          </div>
        )}
      </Col>
    </Row>
  );
};
